import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  TrendingUp,
  Brain,
  Bell,
  LineChart,
  Wallet,
  Globe,
  Shield,
  Zap,
  BarChart3,
  ArrowRight,
  Check,
  Star,
  ChevronRight,
  Activity,
  Eye,
  EyeOff,
  Menu,
  X,
  Sun,
  Moon,
} from "lucide-react";

// Hook for counting animation
function useCountUp(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      animateCount();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
          animateCount();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted, startOnView]);

  const animateCount = () => {
    const startTime = Date.now();
    const endValue = parseFloat(end.replace(/[^0-9.]/g, ""));
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(endValue * easeOut);
      
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(animate);
  };

  return { count, ref };
}

// Animated counter component
function AnimatedCounter({ value, label, format }) {
  const { count, ref } = useCountUp(value, 2000);
  
  // Format the count based on the format type
  const formatCount = () => {
    if (format === "money") {
      return `$${count}M+`;
    } else if (format === "k") {
      return `${count}K+`;
    } else {
      return `${count.toLocaleString()}+`;
    }
  };

  return (
    <div ref={ref} className="text-center" data-testid={`social-proof-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="font-heading text-3xl lg:text-4xl font-bold text-zinc-100">
        {formatCount()}
      </div>
      <div className="text-xs text-zinc-500 uppercase tracking-wider font-mono mt-1">
        {label}
      </div>
    </div>
  );
}

// Animated price ticker data
const TICKER_DATA = [
  { name: "COCOA", price: "$3,974", change: "+2.36%" },
  { name: "GOLD", price: "$2,387", change: "+1.82%" },
  { name: "COFFEE", price: "$2,994", change: "-1.24%" },
  { name: "WHEAT", price: "$568", change: "+0.95%" },
  { name: "CRUDE OIL", price: "$81.20", change: "-0.45%" },
  { name: "COPPER", price: "$4.62", change: "+1.34%" },
  { name: "SUGAR", price: "$17.42", change: "+0.67%" },
  { name: "COTTON", price: "$72.85", change: "-0.89%" },
];

// Features data
const FEATURES = [
  {
    icon: Brain,
    title: "AI-Powered Intelligence",
    description: "Get AI-generated market insights, price predictions, and investment recommendations powered by advanced LLMs.",
    color: "#00ff41",
  },
  {
    icon: LineChart,
    title: "Real-Time Price Streaming",
    description: "Monitor live commodity prices with WebSocket-powered updates. Never miss a market movement.",
    color: "#0ea5e9",
  },
  {
    icon: Bell,
    title: "Smart Price Alerts",
    description: "Set custom alerts for price thresholds. Get notified via push notifications and email instantly.",
    color: "#eab308",
  },
  {
    icon: Wallet,
    title: "Portfolio Tracking",
    description: "Track your positions with real-time P&L calculations, performance charts, and paper trading.",
    color: "#a855f7",
  },
  {
    icon: BarChart3,
    title: "Technical Analysis",
    description: "Deep dive into historical data with SMA, EMA, RSI, and MACD indicators at your fingertips.",
    color: "#f97316",
  },
  {
    icon: Globe,
    title: "Global Trade Flow",
    description: "Visualize trade corridors, track exporters and importers, and understand global market dynamics.",
    color: "#06b6d4",
  },
];

// How it works steps
const STEPS = [
  {
    step: "01",
    title: "Connect",
    description: "Sign up and connect to our real-time market data feeds covering 14+ soft and hard commodities.",
  },
  {
    step: "02",
    title: "Analyze",
    description: "Use our AI-powered tools to analyze markets, get predictions, and identify trading opportunities.",
  },
  {
    step: "03",
    title: "Act",
    description: "Set alerts, track your portfolio, and make informed decisions with confidence.",
  },
];

// Pricing plans
const PRICING = [
  {
    name: "Starter",
    price: "$0",
    period: "/month",
    description: "For individual traders exploring commodities",
    features: [
      "Real-time prices for 5 commodities",
      "Basic price alerts (5 max)",
      "7-day historical data",
      "Single watchlist",
      "Community support",
    ],
    cta: "Get Started Free",
    popular: false,
  },
  {
    name: "Professional",
    price: "$49",
    period: "/month",
    description: "For serious traders and analysts",
    features: [
      "All 14+ commodities real-time",
      "Unlimited price alerts",
      "1-year historical data",
      "AI-powered insights",
      "Portfolio tracking",
      "Technical indicators (SMA, RSI, MACD)",
      "Multiple watchlists",
      "Email & push notifications",
      "Priority support",
    ],
    cta: "Start 14-Day Trial",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For institutions and trading desks",
    features: [
      "Everything in Professional",
      "API access",
      "Custom data feeds",
      "Dedicated account manager",
      "White-label options",
      "SLA guarantees",
      "Custom integrations",
      "On-premise deployment",
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

// Testimonials
const TESTIMONIALS = [
  {
    quote: "UWATU transformed how we analyze commodity markets. The AI insights have given us a significant edge.",
    author: "Sarah Chen",
    role: "Head of Trading, Meridian Capital",
    avatar: "SC",
  },
  {
    quote: "The real-time alerts and portfolio tracking features are exactly what we needed. Highly recommended.",
    author: "Marcus Johnson",
    role: "Commodity Analyst, Global Trade Co",
    avatar: "MJ",
  },
  {
    quote: "Finally, a platform that combines technical analysis with AI-powered intelligence. Game changer.",
    author: "Elena Rodriguez",
    role: "Senior Trader, Apex Investments",
    avatar: "ER",
  },
];

// Stats
const STATS = [
  { value: "14+", label: "Commodities" },
  { value: "500ms", label: "Latency" },
  { value: "99.9%", label: "Uptime" },
  { value: "24/7", label: "Market Coverage" },
];

// Social proof stats with numeric values for animation
const SOCIAL_PROOF = [
  { value: "10000", label: "Active Traders", format: "number" },
  { value: "50", label: "Portfolio Tracked", format: "money" },
  { value: "500", label: "Alerts Triggered", format: "k" },
  { value: "45", label: "Countries", format: "number" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { email, password, name };
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      try {
        await api.post("/seed");
      } catch (_) {}
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenu(false);
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#09090b] text-zinc-100 overflow-x-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/uwatu-logo.png" alt="UWATU" className="w-10 h-10 object-contain" />
              <div>
                <span className="font-heading text-lg font-bold uppercase tracking-wider">UWATU</span>
                <span className="hidden sm:inline text-[10px] text-zinc-500 ml-2 uppercase tracking-widest">
                  Commodities Intelligence
                </span>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection("features")}
                className="text-sm text-zinc-400 hover:text-[#00ff41] transition-colors font-mono"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="text-sm text-zinc-400 hover:text-[#00ff41] transition-colors font-mono"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-sm text-zinc-400 hover:text-[#00ff41] transition-colors font-mono"
              >
                Pricing
              </button>
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                className="text-zinc-300 hover:text-[#00ff41] font-mono text-sm"
                onClick={() => {
                  setIsLogin(true);
                  setShowAuth(true);
                }}
              >
                Login
              </Button>
              <Button
                data-testid="nav-signup-btn"
                className="bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-sm rounded-none"
                onClick={() => {
                  setIsLogin(false);
                  setShowAuth(true);
                }}
              >
                Get Started
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-zinc-400 hover:text-zinc-100"
              onClick={() => setMobileMenu(!mobileMenu)}
            >
              {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenu && (
          <div className="md:hidden bg-[#0a0a0c] border-t border-zinc-800 px-4 py-4 space-y-3">
            <button
              onClick={() => scrollToSection("features")}
              className="block w-full text-left text-sm text-zinc-400 hover:text-[#00ff41] py-2 font-mono"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection("how-it-works")}
              className="block w-full text-left text-sm text-zinc-400 hover:text-[#00ff41] py-2 font-mono"
            >
              How It Works
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="block w-full text-left text-sm text-zinc-400 hover:text-[#00ff41] py-2 font-mono"
            >
              Pricing
            </button>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono text-sm rounded-none"
                onClick={() => {
                  setIsLogin(true);
                  setShowAuth(true);
                  setMobileMenu(false);
                }}
              >
                Login
              </Button>
              <Button
                className="flex-1 bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-sm rounded-none"
                onClick={() => {
                  setIsLogin(false);
                  setShowAuth(true);
                  setMobileMenu(false);
                }}
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#00ff41]/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#00ff41]/5 rounded-full blur-[120px] opacity-30" />
        
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(#00ff41 1px, transparent 1px), linear-gradient(90deg, #00ff41 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Live badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/80 border border-zinc-800 mb-8">
              <div className="w-2 h-2 bg-[#00ff41] rounded-full animate-pulse" />
              <span className="text-[11px] text-zinc-400 font-mono uppercase tracking-wider">
                Markets Live • 14+ Commodities
              </span>
            </div>

            {/* Main headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-zinc-100 mb-6">
              <span className="block">Commodities Intelligence</span>
              <span className="block text-[#00ff41] mt-2">Powered by AI</span>
            </h1>

            <p className="text-base lg:text-lg text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Real-time market data, AI-driven insights, and powerful analytics for soft and hard commodities. 
              Make smarter trading decisions with UWATU.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Button
                data-testid="hero-get-started-btn"
                size="lg"
                className="bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-sm uppercase tracking-wider rounded-none px-8 h-12 w-full sm:w-auto"
                onClick={() => {
                  setIsLogin(false);
                  setShowAuth(true);
                }}
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-demo-btn"
                size="lg"
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-600 font-mono text-sm uppercase tracking-wider rounded-none px-8 h-12 w-full sm:w-auto"
                onClick={() => scrollToSection("features")}
              >
                Watch Demo
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {STATS.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="font-heading text-3xl lg:text-4xl font-bold text-[#00ff41]">{stat.value}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider font-mono mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Price Ticker */}
      <div className="relative border-y border-zinc-800 bg-[#0a0a0c]/50 overflow-hidden py-4">
        <div className="animate-ticker flex gap-12">
          {[...TICKER_DATA, ...TICKER_DATA].map((item, i) => (
            <div key={i} className="flex items-center gap-3 whitespace-nowrap">
              <span className="text-xs font-mono text-zinc-500">{item.name}</span>
              <span className="text-sm font-mono text-zinc-100">{item.price}</span>
              <span
                className={`text-xs font-mono ${
                  item.change.startsWith("+") ? "text-[#00ff41]" : "text-[#ff3b30]"
                }`}
              >
                {item.change}
              </span>
            </div>
          ))}
        </div>
        <style>{`
          @keyframes ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-ticker {
            animation: ticker 30s linear infinite;
          }
        `}</style>
      </div>

      {/* Platform Preview */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Terminal window frame */}
            <div className="border border-zinc-800 bg-[#0a0a0c] rounded-none overflow-hidden shadow-2xl shadow-[#00ff41]/5">
              {/* Terminal header */}
              <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#ff3b30]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#eab308]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00ff41]" />
                </div>
                <span className="text-[10px] text-zinc-500 font-mono tracking-wider">
                  UWATU:~/dashboard
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#00ff41] rounded-full animate-pulse" />
                  <span className="text-[10px] text-[#00ff41] font-mono">LIVE</span>
                </div>
              </div>
              
              {/* Mock dashboard preview */}
              <div className="p-6 lg:p-8 space-y-6">
                {/* Header row */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-xl uppercase tracking-wider text-zinc-100">
                      Africa Market Overview
                    </h3>
                    <p className="text-xs text-zinc-500 font-mono mt-1">Real-time commodities data</p>
                  </div>
                  <Badge className="bg-[#00ff41]/10 text-[#00ff41] border-[#00ff41]/30 rounded-none text-[10px]">
                    67% BULLISH
                  </Badge>
                </div>

                {/* Commodity cards grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { name: "Cocoa", price: "$3,974", change: "+2.36%", positive: true },
                    { name: "Coffee", price: "$2,994", change: "-1.24%", positive: false },
                    { name: "Wheat", price: "$568", change: "+0.95%", positive: true },
                    { name: "Gold", price: "$2,387", change: "+1.82%", positive: true },
                    { name: "Crude Oil", price: "$81.20", change: "-0.45%", positive: false },
                    { name: "Copper", price: "$4.62", change: "+1.34%", positive: true },
                  ].map((c, i) => (
                    <div
                      key={i}
                      className="p-4 bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                    >
                      <div className="text-[10px] text-zinc-500 uppercase font-mono mb-2">{c.name}</div>
                      <div className="text-lg font-mono text-zinc-100">{c.price}</div>
                      <div
                        className={`text-xs font-mono mt-1 ${
                          c.positive ? "text-[#00ff41]" : "text-[#ff3b30]"
                        }`}
                      >
                        {c.change}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart area */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 p-4 bg-zinc-900/30 border border-zinc-800">
                    <div className="text-[10px] text-zinc-500 uppercase font-mono mb-4">Price History</div>
                    <div className="h-48 flex items-end justify-around gap-1">
                      {Array.from({ length: 24 }, (_, i) => (
                        <div
                          key={i}
                          className="w-full bg-[#00ff41]/20 hover:bg-[#00ff41]/40 transition-colors"
                          style={{ height: `${Math.random() * 80 + 20}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="p-4 bg-zinc-900/30 border border-zinc-800">
                    <div className="text-[10px] text-zinc-500 uppercase font-mono mb-4">Top Gainers</div>
                    <div className="space-y-3">
                      {[
                        { name: "Cocoa", change: "+2.36%" },
                        { name: "Gold", change: "+1.82%" },
                        { name: "Copper", change: "+1.34%" },
                      ].map((g, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm text-zinc-300 font-mono">{g.name}</span>
                          <span className="text-sm text-[#00ff41] font-mono">{g.change}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#00ff41]/10 text-[#00ff41] border-[#00ff41]/30 rounded-none text-[10px] mb-4">
              FEATURES
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 mb-4">
              Everything You Need to Trade Smarter
            </h2>
            <p className="text-base text-zinc-400">
              From real-time data to AI-powered insights, UWATU gives you the complete toolkit for commodities intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={i}
                  data-testid={`feature-card-${i}`}
                  className="bg-[#0a0a0c] border-zinc-800 hover:border-zinc-700 rounded-none transition-all duration-300 group"
                >
                  <CardContent className="p-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center border border-zinc-800 mb-4 group-hover:border-opacity-50 transition-colors"
                      style={{ borderColor: feature.color }}
                    >
                      <Icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-zinc-100 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/30 rounded-none text-[10px] mb-4">
              HOW IT WORKS
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-base text-zinc-400">
              Three simple steps to transform your commodities trading experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="relative">
                {/* Connector line */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[1px] bg-gradient-to-r from-zinc-700 to-transparent" />
                )}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 border border-zinc-800 bg-[#0a0a0c] mb-6">
                    <span className="font-heading text-3xl font-bold text-[#00ff41]">{step.step}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold uppercase tracking-wider text-zinc-100 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/30 rounded-none text-[10px] mb-4">
              TESTIMONIALS
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 mb-4">
              Trusted by Traders Worldwide
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Card
                key={i}
                className="bg-[#0a0a0c] border-zinc-800 rounded-none"
              >
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#eab308] text-[#eab308]" />
                    ))}
                  </div>
                  <p className="text-sm text-zinc-300 mb-6 leading-relaxed italic">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center text-xs font-mono text-[#00ff41]">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-zinc-100">{t.author}</div>
                      <div className="text-xs text-zinc-500">{t.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 border-y border-zinc-800 bg-[#0a0a0c]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm text-zinc-500 uppercase tracking-wider font-mono">
              Trusted by traders and institutions worldwide
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {SOCIAL_PROOF.map((stat, i) => (
              <AnimatedCounter key={i} value={stat.value} label={stat.label} format={stat.format} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#eab308]/10 text-[#eab308] border-[#eab308]/30 rounded-none text-[10px] mb-4">
              PRICING
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base text-zinc-400">
              Choose the plan that fits your trading needs. All plans include a 14-day free trial.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <Card
                key={i}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                className={`bg-[#0a0a0c] rounded-none relative ${
                  plan.popular
                    ? "border-[#00ff41] border-2"
                    : "border-zinc-800"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#00ff41] text-black rounded-none text-[10px] px-3">
                      MOST POPULAR
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-zinc-100 mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-heading text-4xl font-bold text-zinc-100">{plan.price}</span>
                      {plan.period && <span className="text-sm text-zinc-500">{plan.period}</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">{plan.description}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#00ff41] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-zinc-400">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full rounded-none font-mono text-sm uppercase tracking-wider ${
                      plan.popular
                        ? "bg-[#00ff41] text-black hover:bg-[#00cc33]"
                        : "bg-zinc-800 text-zinc-100 hover:bg-zinc-700"
                    }`}
                    onClick={() => {
                      setIsLogin(false);
                      setShowAuth(true);
                    }}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 bg-zinc-950/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#00ff41]/5 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#00ff41]/5 rounded-full blur-[100px] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 mb-6">
            Ready to Transform Your Trading?
          </h2>
          <p className="text-base lg:text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
            Join thousands of traders using UWATU to make smarter, data-driven decisions in commodity markets.
          </p>
          <Button
            data-testid="final-cta-btn"
            size="lg"
            className="bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-sm uppercase tracking-wider rounded-none px-12 h-14"
            onClick={() => {
              setIsLogin(false);
              setShowAuth(true);
            }}
          >
            Start Your Free Trial
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-xs text-zinc-500 mt-4 font-mono">No credit card required • 14-day free trial</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-12 bg-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src="/uwatu-logo.png" alt="UWATU" className="w-10 h-10 object-contain" />
                <div>
                  <span className="font-heading text-lg font-bold uppercase tracking-wider">UWATU</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                AI-powered commodities intelligence platform for smarter trading decisions.
              </p>
            </div>
            <div>
              <h4 className="text-xs text-zinc-400 uppercase tracking-wider font-mono mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection("features")} className="text-sm text-zinc-500 hover:text-zinc-300">Features</button></li>
                <li><button onClick={() => scrollToSection("pricing")} className="text-sm text-zinc-500 hover:text-zinc-300">Pricing</button></li>
                <li><span className="text-sm text-zinc-500">API Docs</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs text-zinc-400 uppercase tracking-wider font-mono mb-4">Company</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-zinc-500">About</span></li>
                <li><span className="text-sm text-zinc-500">Blog</span></li>
                <li><span className="text-sm text-zinc-500">Careers</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs text-zinc-400 uppercase tracking-wider font-mono mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-zinc-500">Privacy</span></li>
                <li><span className="text-sm text-zinc-500">Terms</span></li>
                <li><span className="text-sm text-zinc-500">Security</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs text-zinc-600 font-mono">
              © 2024 UWATU. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <span className="text-[9px] text-zinc-600 font-mono">DATA</span>
              <span className="text-[9px] text-zinc-600 font-mono">INSIGHTS</span>
              <span className="text-[9px] text-zinc-600 font-mono">PRICING</span>
              <span className="text-[9px] text-zinc-600 font-mono">RISK</span>
              <span className="text-[9px] text-[#00ff41] font-mono">CAPITAL</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-[#0a0a0c] border-zinc-800 rounded-none max-w-md p-0">
          <DialogTitle className="sr-only">{isLogin ? "Login to UWATU" : "Create UWATU Account"}</DialogTitle>
          <DialogDescription className="sr-only">
            {isLogin ? "Enter your credentials to access your account" : "Sign up for a new UWATU account"}
          </DialogDescription>
          {/* Terminal header */}
          <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#ff3b30]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#eab308]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#00ff41]" />
            </div>
            <span className="text-[10px] text-zinc-500 font-mono tracking-wider uppercase">
              uwatu:~/{isLogin ? "login" : "register"}
            </span>
          </div>

          <div className="p-6">
            {/* Logo */}
            <div className="flex items-center gap-4 mb-6">
              <img src="/uwatu-logo.png" alt="UWATU" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wider text-zinc-100">
                  UWATU
                </h1>
                <p className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-mono">
                  Commodities Intelligence
                </p>
              </div>
            </div>

            {/* Boot sequence text */}
            <div className="mb-5 font-mono text-[11px] space-y-1">
              <p className="text-zinc-500">$ initializing market intelligence engine...</p>
              <p className="text-[#00ff41]">$ ready</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                    Full Name
                  </label>
                  <Input
                    data-testid="modal-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600"
                    required
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                  Email Address
                </label>
                <Input
                  data-testid="modal-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Input
                    data-testid="modal-password-input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600 pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    onClick={() => setShowPw(!showPw)}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                data-testid="modal-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full rounded-none bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-xs uppercase tracking-wider h-10"
              >
                {loading ? "Processing..." : isLogin ? "Login" : "Create Account"}
              </Button>
            </form>

            <div className="mt-5 text-center">
              <button
                data-testid="modal-toggle-auth-btn"
                className="text-[11px] text-zinc-500 hover:text-[#00ff41] font-mono transition-colors"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? "$ create new account" : "$ login to existing account"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
